import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { EmptyState, ErrorState, Money, SkeletonRows, Status, Surface, Table, TableContainer, TBody, Td, Th, THead, Tr, cn, focusRing } from "../../../../components/ui";
import { FINANCE_CORRECTIONS_PATH, HR_CORRECTIONS_PATH, adjustmentsApi, classificationLabel, stateOf } from "./adjustmentView";

// Report 54B (D2): corrections to paid payslips, as a work queue. Open cases first, grouped by who acts next; closed
// ones after. `area` is "hr" or "finance": the same list, read with each team's next action in front.

const CLOSED = (item) => ["REJECTED", "CANCELLED"].includes(item.status) || (item.status === "APPROVED" && ["SETTLED", "CANCELLED"].includes(item.settlementStatus));
const withFinance = (item) => item.status === "SUBMITTED" || (item.status === "APPROVED" && !CLOSED(item));

function Rows({ items, base }) {
  return (
    <TableContainer>
      <Table caption="Corrections">
        <THead>
          <Tr>
            <Th>Employee</Th>
            <Th>Correction</Th>
            <Th>State</Th>
            <Th numeric>Amount</Th>
            <Th numeric>Outstanding</Th>
          </Tr>
        </THead>
        <TBody>
          {items.map((item) => {
            const state = stateOf(item);
            return (
              <Tr key={item.id}>
                <Td>
                  <Link to={`${base}/${item.id}`} className={cn("rounded-control font-medium text-accent hover:underline", focusRing)}>{item.memberName}</Link>
                  <span className="block text-meta text-secondary">{item.periodName}</span>
                </Td>
                <Td>
                  {classificationLabel(item.classification)}
                  <span className="block text-meta text-secondary">{item.number} · {item.direction === "PAY_EMPLOYEE" ? "owed to the employee" : "claimed from the employee"}</span>
                </Td>
                <Td><Status tone={state.tone} icon={state.tone === "danger"}>{state.label}</Status></Td>
                <Td numeric>{item.statutoryState === "REVIEW_REQUIRED" ? <span className="text-secondary">Not decided</span> : <Money value={item.amountCents} />}</Td>
                <Td numeric><Money value={item.outstandingCents} /></Td>
              </Tr>
            );
          })}
        </TBody>
      </Table>
    </TableContainer>
  );
}

export default function CorrectionsList({ area = "hr" }) {
  const [state, setState] = useState({ status: "loading", items: [] });
  const base = area === "finance" ? FINANCE_CORRECTIONS_PATH : HR_CORRECTIONS_PATH;
  const load = useCallback(async () => {
    try {
      setState({ status: "ready", items: await adjustmentsApi.list() });
    } catch {
      setState({ status: "error", items: [] });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    const open = state.items.filter((item) => !CLOSED(item));
    const mineFirst = area === "finance" ? open.filter(withFinance) : open.filter((item) => !withFinance(item));
    const others = open.filter((item) => !mineFirst.includes(item));
    return [
      { key: "mine", title: area === "finance" ? "Needs Finance" : "Needs HR", items: mineFirst },
      { key: "others", title: area === "finance" ? "With HR" : "With Finance", items: others },
      { key: "closed", title: "Closed", items: state.items.filter(CLOSED) },
    ].filter((group) => group.items.length);
  }, [state.items, area]);

  if (state.status === "loading") return <Surface><SkeletonRows rows={4} columns={5} label="Loading corrections" /></Surface>;
  if (state.status === "error") return <Surface><ErrorState title="Corrections couldn't be loaded" description="Check your connection and try again." onRetry={load} /></Surface>;
  if (!state.items.length) {
    return (
      <Surface>
        <EmptyState
          title="No corrections"
          description={area === "finance"
            ? "When HR raises a correction to a paid payslip, it appears here for a Finance decision."
            : "A correction is raised from a paid payslip: open the payslip in History and choose Raise a correction."}
        />
      </Surface>
    );
  }
  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <section key={group.key} aria-label={group.title}>
          <h2 className="mb-2 text-section-title text-foreground">{group.title} <span className="font-normal text-secondary">({group.items.length})</span></h2>
          <Surface><Rows items={group.items} base={base} /></Surface>
        </section>
      ))}
    </div>
  );
}
